// Ввод тренировки задним числом и правка прошедшей: дата, время, подходы таблицей.
// Тренировка и подходы сохраняются PUT по UUID, созданным здесь, — повтор сохранения не плодит дублей.

import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { PageHeader } from '@/components/layout'
import { Button, Card, EmptyState, ErrorText, IconButton, Input, PageSpinner, Select, useToast } from '@/components/ui'
import { cn } from '@/lib/cn'
import { ExercisePicker } from '@/pages/exercises/ExercisePicker'

import { trainingApi, useInvalidateTraining, useSession, useTemplates, type Session } from './api'
import {
  buildPayload,
  copySet,
  editorFromSession,
  emptyEditor,
  emptySet,
  exercisesFromTemplate,
  newId,
  removedSetIds,
  setFields,
  type EditorErrors,
  type EditorExercise,
  type EditorPayload,
  type EditorSet,
  type EditorState,
} from './logic'

const cell =
  'h-10 w-full min-w-0 rounded-md border border-border bg-surface px-2 text-sm tabular-nums text-fg focus:outline-2 focus:outline-accent'

type TextKey = 'weight' | 'reps' | 'duration' | 'distance'

function ExerciseTable({
  exercise,
  onChange,
  onRemove,
}: {
  exercise: EditorExercise
  onChange: (next: EditorExercise) => void
  onRemove: () => void
}) {
  const fields = setFields(exercise.tracking)
  const columns: { key: TextKey; label: string }[] = []
  if (fields.weight) columns.push({ key: 'weight', label: exercise.tracking === 'bodyweight_reps' ? 'Доп. вес, кг' : 'Вес, кг' })
  if (fields.reps) columns.push({ key: 'reps', label: 'Повторы' })
  if (fields.duration) columns.push({ key: 'duration', label: 'Время, м:сс' })
  if (fields.distance) columns.push({ key: 'distance', label: 'Дистанция, м' })

  const update = (index: number, patch: Partial<EditorSet>) =>
    onChange({ ...exercise, sets: exercise.sets.map((s, i) => (i === index ? { ...s, ...patch } : s)) })

  return (
    <Card title={exercise.name} actions={<IconButton icon="close" label="Убрать упражнение" onClick={onRemove} />}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[22rem] text-sm">
          <thead>
            <tr className="text-left text-xs text-muted">
              <th className="w-8 pb-1 font-medium">#</th>
              {columns.map((c) => (
                <th key={c.key} className="pb-1 pr-2 font-medium">
                  {c.label}
                </th>
              ))}
              <th className="pb-1 pr-2 text-center font-medium">Разминка</th>
              <th className="pb-1 pr-2 text-center font-medium">Выполнен</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {exercise.sets.map((set, index) => (
              <tr key={set.id} className={cn(!set.completed && 'opacity-60')}>
                <td className="py-1 tabular-nums text-muted">{index + 1}</td>
                {columns.map((c) => (
                  <td key={c.key} className="py-1 pr-2">
                    <input
                      className={cell}
                      inputMode={c.key === 'duration' ? 'text' : 'decimal'}
                      aria-label={`${c.label}, подход ${index + 1}`}
                      value={set[c.key]}
                      onChange={(e) => update(index, { [c.key]: e.target.value })}
                    />
                  </td>
                ))}
                <td className="py-1 pr-2 text-center">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    aria-label={`Разминочный, подход ${index + 1}`}
                    checked={set.is_warmup}
                    onChange={(e) => update(index, { is_warmup: e.target.checked })}
                  />
                </td>
                <td className="py-1 pr-2 text-center">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    aria-label={`Выполнен, подход ${index + 1}`}
                    checked={set.completed}
                    onChange={(e) => update(index, { completed: e.target.checked })}
                  />
                </td>
                <td className="py-1">
                  <IconButton
                    icon="minus"
                    label="Убрать подход"
                    onClick={() => onChange({ ...exercise, sets: exercise.sets.filter((_, i) => i !== index) })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button
        size="sm"
        variant="ghost"
        icon="plus"
        className="mt-2"
        onClick={() => onChange({ ...exercise, sets: [...exercise.sets, copySet(exercise.sets.at(-1))] })}
      >
        Подход
      </Button>
    </Card>
  )
}

/** Порядок важен: тренировка без окончания → подходы → завершение. Планирование узнаёт
 * о тренировке из POST /finish, когда подходы уже записаны. */
async function saveAll(payload: EditorPayload, id: string, original: Session | null): Promise<void> {
  const endedAt = payload.session.ended_at
  await trainingApi.putSession(id, { ...payload.session, ended_at: null })
  // По одному: рекорды пересчитываются на сервере по порядку подходов.
  for (const s of payload.sets) await trainingApi.putSet(id, s.id, s.data)
  for (const setId of removedSetIds(original, payload)) await trainingApi.deleteSet(id, setId)
  if (!endedAt) return
  if (!original?.ended_at) await trainingApi.finishSession(id, endedAt)
  else if (new Date(original.ended_at).getTime() !== new Date(endedAt).getTime()) {
    await trainingApi.patchSession(id, { ended_at: endedAt })
  }
}

function Editor({ original }: { original: Session | null }) {
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidateTraining()
  const templates = useTemplates(false)
  const [state, setState] = useState<EditorState>(() => (original ? editorFromSession(original) : emptyEditor()))
  const [errors, setErrors] = useState<EditorErrors>({})
  const [picking, setPicking] = useState(false)
  const save = useMutation({
    mutationFn: (payload: EditorPayload) => saveAll(payload, state.id, original),
    onSuccess: async () => {
      await invalidate()
      toast.show({ message: 'Тренировка сохранена', tone: 'accent' })
      navigate(`/workouts/${state.id}`, { replace: true })
    },
  })

  const submit = () => {
    const { payload, errors: problems } = buildPayload(state)
    setErrors(problems)
    if (payload) save.mutate(payload)
  }

  const chooseTemplate = (templateId: string) => {
    const template = templates.data?.find((t) => t.id === templateId)
    setState((s) => ({
      ...s,
      template_id: templateId || null,
      // Упражнения шаблона подставляются, только пока своих ещё нет.
      exercises: template && s.exercises.length === 0 ? exercisesFromTemplate(template) : s.exercises,
    }))
  }

  const setExercise = (index: number, next: EditorExercise) =>
    setState((s) => ({ ...s, exercises: s.exercises.map((e, i) => (i === index ? next : e)) }))

  return (
    <>
      <PageHeader
        title={original ? 'Правка тренировки' : 'Тренировка задним числом'}
        back={original ? { to: `/workouts/${original.id}`, label: 'Тренировка' } : { to: '/workouts', label: 'Тренировки' }}
        actions={
          <Button variant="primary" loading={save.isPending} onClick={submit}>
            Сохранить
          </Button>
        }
      />
      <div className="grid gap-4">
        <Card>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Input label="Дата" type="date" value={state.date} error={errors.date} onChange={(e) => setState({ ...state, date: e.target.value })} />
            <Input label="Начало" type="time" value={state.start} error={errors.start} onChange={(e) => setState({ ...state, start: e.target.value })} />
            <Input
              label="Окончание"
              type="time"
              value={state.end}
              error={errors.end}
              hint="Пусто — тренировка ещё идёт"
              onChange={(e) => setState({ ...state, end: e.target.value })}
            />
            <Select
              label="Шаблон"
              placeholder="Без шаблона"
              value={state.template_id ?? ''}
              onChange={(e) => chooseTemplate(e.target.value)}
              options={(templates.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
            />
          </div>
          <Input
            containerClassName="mt-4"
            label="Заметка"
            value={state.notes}
            onChange={(e) => setState({ ...state, notes: e.target.value })}
          />
        </Card>
        {state.exercises.length === 0 ? (
          <Card>
            <EmptyState icon="workout" title="Добавь упражнения" description="Или выбери шаблон — подставятся его упражнения и подходы." />
          </Card>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {state.exercises.map((ex, index) => (
              <ExerciseTable
                key={ex.key}
                exercise={ex}
                onChange={(next) => setExercise(index, next)}
                onRemove={() => setState((s) => ({ ...s, exercises: s.exercises.filter((_, i) => i !== index) }))}
              />
            ))}
          </div>
        )}
        <div>
          <Button icon="plus" onClick={() => setPicking(true)}>
            Добавить упражнение
          </Button>
        </div>
        <ErrorText>{errors.sets}</ErrorText>
        <ErrorText error={save.error} />
      </div>
      {picking ? (
        <ExercisePicker
          open
          onClose={() => setPicking(false)}
          onPick={(ex) =>
            setState((s) => ({
              ...s,
              exercises: [
                ...s.exercises,
                { key: newId(), exercise_id: ex.id, name: ex.name, tracking: ex.tracking, sets: [emptySet()] },
              ],
            }))
          }
        />
      ) : null}
    </>
  )
}

export function SessionEditorPage() {
  const { id } = useParams()
  const session = useSession(id ?? null)
  if (!id) return <Editor original={null} />
  if (session.isPending) return <PageSpinner />
  if (session.error) return <ErrorText error={session.error} />
  return <Editor key={session.data.id} original={session.data} />
}
